import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, day } from '../../lib'
import { Loading } from '../../app/ui'
import { Err, PartnerPage, useAction } from './common'

type Members = {
  members: { id: string; email: string; name: string | null; role: string; created_at: string }[]
  invites: { id: string; email: string; role: string; expires_at: string }[]
}

const ROLE_LABEL: Record<string, string> = { owner: 'Owner', admin: 'Admin', viewer: 'Viewer' }

export function TeamPage() {
  return <PartnerPage>{(pid, role) => <TeamBody pid={pid} role={role} />}</PartnerPage>
}

function TeamBody({ pid, role }: { pid: string; role: string }) {
  const q = useQuery({ queryKey: ['partners', 'members', pid], queryFn: () => api<Members>('GET', `/api/partners/${pid}/members`) })
  const [email, setEmail] = useState('')
  const [newRole, setNewRole] = useState<'admin' | 'viewer'>('admin')
  const a = useAction(() => api('POST', `/api/partners/${pid}/invites`, { email, role: newRole }), () => setEmail(''))
  const revoke = useAction((iid: string) => api('DELETE', `/api/partners/${pid}/invites/${iid}`))
  const canWrite = role !== 'viewer'
  if (!q.data) return <Loading />
  return (
    <div className="stack">
      <h1>Team</h1>
      <div className="table-wrap">
        <table data-testid="partners-members">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Since</th>
              {canWrite ? <th /> : null}
            </tr>
          </thead>
          <tbody>
            {q.data.members.map((m) => (
              <tr key={m.id}>
                <td>{m.email}</td>
                <td>{ROLE_LABEL[m.role] ?? m.role}</td>
                <td>{day(m.created_at)}</td>
                {canWrite ? <td /> : null}
              </tr>
            ))}
            {q.data.invites.map((i) => (
              <tr key={i.id} className="muted">
                <td>{i.email}</td>
                <td>{ROLE_LABEL[i.role] ?? i.role} (invited)</td>
                <td>expires {day(i.expires_at)}</td>
                {canWrite ? (
                  <td>
                    <button
                      className="link-button"
                      onClick={() => revoke.run(i.id)}
                      disabled={revoke.busy}
                      aria-label={`Revoke invite for ${i.email}`}
                      data-testid="partners-invite-revoke"
                    >
                      Revoke
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Err error={revoke.error} />
      {canWrite && (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault()
            a.run(undefined)
          }}
        >
          <input data-testid="partners-invite-email" type="email" style={{ flexBasis: '16rem' }} placeholder="teammate@yourco.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <select data-testid="partners-invite-role" value={newRole} onChange={(e) => setNewRole(e.target.value as 'admin' | 'viewer')}>
            <option value="admin">Admin</option>
            <option value="viewer">Viewer</option>
          </select>
          <button className="primary" data-testid="partners-invite-send" disabled={a.busy}>
            Invite
          </button>
        </form>
      )}
      <Err error={a.error} />
    </div>
  )
}
